import React, { useState, useEffect, useCallback } from 'react';
import {
  BadgeTypeModel,
  ApiKeyModel,
  DashboardStats,
} from './types';
import { api, ParticipantEnriched } from './services/api';
import { Navigation } from './components/Navigation';
import { DashboardView } from './components/DashboardView';
import { ParticipantsView } from './components/ParticipantsView';
import { BadgeTypesView } from './components/BadgeTypesView';
import { ScannerView } from './components/ScannerView';
import { ApiManagementView } from './components/ApiManagementView';
import { ParticipantBadgeView } from './components/ParticipantBadgeView';
import { SettingsView } from './components/SettingsView';

export function App() {
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [badgeTypes, setBadgeTypes] = useState<BadgeTypeModel[]>([]);
  const [participants, setParticipants] = useState<ParticipantEnriched[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKeyModel[]>([]);
  const [eventName, setEventName] = useState<string>('Live Access Event 2026');
  const [isLiveConnected, setIsLiveConnected] = useState(false);

  // Participant badge view
  const [viewingParticipantId, setViewingParticipantId] = useState<string | null>(null);
  const [preselectedBadgeTypeId, setPreselectedBadgeTypeId] = useState<string | undefined>();

  // Fetch all live data from database
  const refreshData = useCallback(async () => {
    try {
      const [countersData, bts, parts, keys, eventInfo] = await Promise.all([
        api.getCounters(),
        api.getBadgeTypes(),
        api.getParticipants(),
        api.getApiKeys(),
        api.getEventInfo(),
      ]);
      setStats(countersData);
      setBadgeTypes(bts);
      setParticipants(parts);
      setApiKeys(keys);
      if (eventInfo?.eventName) {
        setEventName(eventInfo.eventName);
      }
    } catch (err) {
      console.error('Failed to load live data:', err);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Real-Time Server-Sent Events (SSE) Listener
  useEffect(() => {
    const eventSource = new EventSource('/api/live-stream');

    eventSource.onopen = () => {
      setIsLiveConnected(true);
    };

    eventSource.onerror = () => {
      setIsLiveConnected(false);
    };

    eventSource.addEventListener('COUNTERS_UPDATED', (e) => {
      try {
        const payload = JSON.parse(e.data);
        if (payload.data) {
          setStats(payload.data);
        }
        // Refresh participants & badge types quietly
        api.getParticipants().then(setParticipants).catch(() => {});
        api.getBadgeTypes().then(setBadgeTypes).catch(() => {});
      } catch (err) {}
    });

    return () => {
      eventSource.close();
      setIsLiveConnected(false);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans">
      {/* Minimal Top Navigation */}
      <Navigation
        activeTab={activeTab}
        onSelectTab={(tab) => {
          setActiveTab(tab);
          setViewingParticipantId(null);
        }}
        eventName={eventName}
        isLiveConnected={isLiveConnected}
      />

      {/* Main Body */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-6">
        {viewingParticipantId ? (
          <ParticipantBadgeView
            participantIdOrToken={viewingParticipantId}
            onBack={() => setViewingParticipantId(null)}
          />
        ) : activeTab === 'scanner' ? (
          <ScannerView onScanCompleted={refreshData} />
        ) : activeTab === 'participants' ? (
          <ParticipantsView
            participants={participants}
            badgeTypes={badgeTypes}
            onRefresh={refreshData}
            onOpenBadge={(id) => setViewingParticipantId(id)}
          />
        ) : activeTab === 'badge-types' ? (
          <BadgeTypesView
            badgeTypes={badgeTypes}
            counters={stats?.badgeTypeCounters || []}
            onRefresh={refreshData}
            onOpenApiForType={(btId) => {
              setPreselectedBadgeTypeId(btId);
              setActiveTab('api');
            }}
          />
        ) : activeTab === 'api' ? (
          <ApiManagementView
            badgeTypes={badgeTypes}
            counters={stats?.badgeTypeCounters || []}
            apiKeys={apiKeys}
            totalEntered={stats?.totalEntered || 0}
            onRefresh={refreshData}
            preselectedBadgeTypeId={preselectedBadgeTypeId}
          />
        ) : activeTab === 'settings' ? (
          <SettingsView eventName={eventName} onRefresh={refreshData} />
        ) : (
          <DashboardView
            stats={stats}
            onNavigateToScanner={() => setActiveTab('scanner')}
          />
        )}
      </main>
    </div>
  );
}

export default App;
